import React, { useState, lazy, Suspense } from 'react';
import { notification, message, Modal, Typography, Button, Divider, Row, Col } from 'antd';
import { Form } from 'antd';
import { ErrorRender, FormErrorParser } from '@partsbadger/library';
import ContactStore from 'stores/ContactStore';
import QuotesQuestions from './QuotesQuestions';
import SupportQuestions from './SupportQuestions';
import SalesQuestions from './SalesQuestions';

const { Title } = Typography;

interface IProps {
    visible: boolean;
    setVisible: (value: boolean) => void;
    isQuotes?: boolean;
    isSalesOrder?: boolean;
    name?: string;
    redirectToThisPage?: () => void;
}

const Feedback: React.FC<IProps> = ({ isQuotes, isSalesOrder, name, visible, setVisible, redirectToThisPage }) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [form] = Form.useForm();

    const handleSubmit = async () => {
        form.validateFields()
            .then(async values => {
                setLoading(true);

                const payload = { answers: values, isQuotes: isQuotes, isSalesOrder: isSalesOrder, name: name };

                await ContactStore.sendFeedback(payload)
                    .then(() => {
                        if ((isQuotes || isSalesOrder) && redirectToThisPage) {
                            redirectToThisPage();
                        }
                        notification.success({
                            duration: 5,
                            message: 'Thank you!',
                            description: 'Your feedback was submitted successfully',
                            placement: 'topRight',
                        });

                        setVisible(false);
                        setLoading(false);

                        form.resetFields();
                    })
                    .catch(err => {
                        notification.error({
                            duration: 10,
                            message: 'Error',
                            description: <ErrorRender error={err} />,
                            placement: 'bottomRight',
                        });

                        setLoading(false);
                    });
            })
            .catch(errorInfo => {
                notification.error({
                    key: 'form-errors',
                    duration: 8,
                    message: 'Please input all required fields',
                    description: <FormErrorParser isAntd4 errors={errorInfo.errorFields} />,
                    placement: 'top',
                    maxCount: 1,
                });
            });
    };

    const handleCancel = () => {
        setVisible(false);

        if (isQuotes && redirectToThisPage) {
            redirectToThisPage();
        }
        if (isSalesOrder && redirectToThisPage) {
            redirectToThisPage();
        }
    };

    return (
        <>
            <Modal
                title={
                    <Title level={2} className="mb-0">
                        Share Your Feedback!
                    </Title>
                }
                open={visible}
                footer={null}
                onCancel={() => handleCancel()}
                width={600}
            >
                {isSalesOrder
                    ? `We appreciate your time and interest in sharing your feedback! It will be sent directly to the leadership team. 
                        How would you rate PartsBadger in the following areas on your order?`
                    : `Being Fast, Friendly, Innovative and Responsive is our goal so if we have exceeded or fallen short of
                expectations we want to know! Your feedback will be sent directly to the leadership team. We appreciate
                your time and interest in sharing your feedback.`}
                <Divider />
                <Form form={form} layout="vertical" requiredMark={false}>
                    {isQuotes ? (
                        <QuotesQuestions form={form} />
                    ) : isSalesOrder ?? false ? (
                        <SalesQuestions form={form} />
                    ) : (
                        <SupportQuestions form={form} />
                    )}

                    <Row gutter={[8, 8]}>
                        <Col span={12}>
                            <Button key="back" onClick={() => handleCancel()} block>
                                Dismiss
                            </Button>
                        </Col>
                        <Col span={12}>
                            <Button
                                key="submit"
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                onClick={handleSubmit}
                                block
                            >
                                Submit
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </>
    );
};

export default Feedback;
